// ABOUTME: Renders store, region, recruiter, and job filters for the WhatsApp inbox.
// ABOUTME: Keeps region and job-only controls visibly incompatible in one local surface.
import React from "react";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    makeStyles,
    TextField,
    Typography
} from "@material-ui/core";

import {
    getDisplayText,
    getJobLabel,
    getJobTitle,
    WhatsappInboxFilterOption
} from "./helpers";

interface WhatsappInboxFilterDialogProps {
    open: boolean;
    loading: boolean;
    language: string;
    storeOptions: WhatsappInboxFilterOption[];
    regionOptions: WhatsappInboxFilterOption[];
    recruiterOptions: WhatsappInboxFilterOption[];
    draftStores: WhatsappInboxFilterOption[];
    draftRegions: WhatsappInboxFilterOption[];
    draftRecruiters: WhatsappInboxFilterOption[];
    draftJobs: Compleo.IObject[];
    draftStartedByCurrentUser: boolean;
    jobSearch: string;
    jobResults: Compleo.IObject[];
    jobLoading: boolean;
    jobSearchExecuted: boolean;
    t: (key: string, params?: Record<string, any>) => string;
    onClose: () => void;
    onApply: () => void;
    onClear: () => void;
    onStartedByCurrentUserChange: (value: boolean) => void;
    onToggleStore: (option: WhatsappInboxFilterOption) => void;
    onToggleRegion: (option: WhatsappInboxFilterOption) => void;
    onToggleRecruiter: (option: WhatsappInboxFilterOption) => void;
    onToggleJob: (job: Compleo.IObject) => void;
    onJobSearchChange: (value: string) => void;
    onSearchJobs: () => void;
}

const useStyles = makeStyles((theme) => ({
    section: {
        marginTop: theme.spacing(2)
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: 700,
        textTransform: "uppercase",
        color: "#5d6670",
        marginBottom: theme.spacing(0.5)
    },
    options: {
        maxHeight: 220,
        overflow: "auto",
        border: "1px solid #e0e0e0",
        borderRadius: 4
    },
    jobs: {
        marginTop: theme.spacing(1),
        maxHeight: 360,
        overflow: "auto",
        border: "1px solid #e0e0e0",
        borderRadius: 4
    },
    summary: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(1)
    },
    muted: {
        color: theme.palette.text.secondary
    }
}));

const optionId = (prefix: string, value: string) =>
    `whatsapp-${prefix}-filter-${value.replace(/[^a-zA-Z0-9_-]/g, "")}`;

const WhatsappInboxFilterDialog = (props: WhatsappInboxFilterDialogProps) => {
    const {
        open,
        loading,
        language,
        storeOptions,
        regionOptions,
        recruiterOptions,
        draftStores,
        draftRegions,
        draftRecruiters,
        draftJobs,
        draftStartedByCurrentUser,
        jobSearch,
        jobResults,
        jobLoading,
        jobSearchExecuted,
        t,
        onClose,
        onApply,
        onClear,
        onStartedByCurrentUserChange,
        onToggleStore,
        onToggleRegion,
        onToggleRecruiter,
        onToggleJob,
        onJobSearchChange,
        onSearchJobs
    } = props;
    const classes = useStyles();
    const jobFiltersDisabled = draftRegions.length > 0;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{t("filterConversationsTitle")}</DialogTitle>
            <DialogContent>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={draftStartedByCurrentUser}
                            onChange={(event) =>
                                onStartedByCurrentUserChange(
                                    event.target.checked
                                )
                            }
                            disabled={jobFiltersDisabled}
                            color="primary"
                        />
                    }
                    label={t("startedByMe")}
                />

                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        {t("stores")}
                    </Typography>
                    <List className={classes.options}>
                        {loading && (
                            <ListItem>
                                <Typography className={classes.muted}>
                                    {t("loadingFilters")}
                                </Typography>
                            </ListItem>
                        )}
                        {!loading && storeOptions.length === 0 && (
                            <ListItem>
                                <Typography className={classes.muted}>
                                    {t("noStoresFound")}
                                </Typography>
                            </ListItem>
                        )}
                        {storeOptions.map((store) => {
                            const value = String(store.value || "");
                            const checked = draftStores.some(
                                (item) => String(item.value) === value
                            );
                            const id = optionId("store", value);
                            const counts = [
                                store.jobCount
                                    ? t("jobCount", { count: store.jobCount })
                                    : "",
                                store.conversationCount
                                    ? t("conversationCount", {
                                          count: store.conversationCount
                                      })
                                    : ""
                            ].filter(Boolean);

                            return (
                                <ListItem
                                    key={value}
                                    button
                                    selected={checked}
                                    onClick={() => onToggleStore(store)}
                                >
                                    <Checkbox
                                        edge="start"
                                        checked={checked}
                                        tabIndex={-1}
                                        disableRipple
                                        inputProps={{ "aria-labelledby": id }}
                                    />
                                    <ListItemText
                                        id={id}
                                        primary={store.label}
                                        secondary={
                                            counts.length
                                                ? counts.join(" · ")
                                                : undefined
                                        }
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>

                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        {t("regions")}
                    </Typography>
                    <List className={classes.options}>
                        {loading && (
                            <ListItem>
                                <Typography className={classes.muted}>
                                    {t("loadingFilters")}
                                </Typography>
                            </ListItem>
                        )}
                        {!loading && regionOptions.length === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary={t("noRegionsFound")}
                                    secondary={t("noRegionsAccessHelp")}
                                />
                            </ListItem>
                        )}
                        {regionOptions.map((region) => {
                            const value = String(region.value || "");
                            const checked = draftRegions.some(
                                (item) => String(item.value) === value
                            );
                            const id = optionId("region", value);

                            return (
                                <ListItem
                                    key={value}
                                    button
                                    selected={checked}
                                    onClick={() => onToggleRegion(region)}
                                >
                                    <Checkbox
                                        edge="start"
                                        checked={checked}
                                        tabIndex={-1}
                                        disableRipple
                                        inputProps={{ "aria-labelledby": id }}
                                    />
                                    <ListItemText
                                        id={id}
                                        primary={region.label}
                                        secondary={
                                            region.conversationCount
                                                ? t("conversationCount", {
                                                      count:
                                                          region.conversationCount
                                                  })
                                                : undefined
                                        }
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                    <Typography className={classes.muted} variant="caption">
                        {t("regionFilterExclusivity")}
                    </Typography>
                </Box>

                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        {t("mainRecruiters")}
                    </Typography>
                    <List className={classes.options}>
                        {loading && (
                            <ListItem>
                                <Typography className={classes.muted}>
                                    {t("loadingFilters")}
                                </Typography>
                            </ListItem>
                        )}
                        {!loading && recruiterOptions.length === 0 && (
                            <ListItem>
                                <Typography className={classes.muted}>
                                    {t("noRecruitersFound")}
                                </Typography>
                            </ListItem>
                        )}
                        {recruiterOptions.map((recruiter) => {
                            const email = String(
                                recruiter.email || recruiter.value || ""
                            );
                            const checked = draftRecruiters.some(
                                (item) =>
                                    String(item.email || item.value) === email
                            );
                            const id = optionId("recruiter", email);

                            return (
                                <ListItem
                                    key={email}
                                    button
                                    disabled={jobFiltersDisabled}
                                    selected={checked}
                                    onClick={() => onToggleRecruiter(recruiter)}
                                >
                                    <Checkbox
                                        edge="start"
                                        checked={checked}
                                        disabled={jobFiltersDisabled}
                                        tabIndex={-1}
                                        disableRipple
                                        inputProps={{ "aria-labelledby": id }}
                                    />
                                    <ListItemText
                                        id={id}
                                        primary={recruiter.label}
                                        secondary={
                                            recruiter.jobCount
                                                ? t("jobCount", {
                                                      count: recruiter.jobCount
                                                  })
                                                : email
                                        }
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>

                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        {t("jobs")}
                    </Typography>
                    <TextField
                        autoFocus={!jobFiltersDisabled}
                        variant="outlined"
                        size="small"
                        label={t("jobCodeOrTitle")}
                        value={jobSearch}
                        disabled={jobFiltersDisabled}
                        onChange={(event) =>
                            onJobSearchChange(event.target.value)
                        }
                        fullWidth
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                onSearchJobs();
                            }
                        }}
                    />
                    {draftJobs.length > 0 && (
                        <Box className={classes.summary}>
                            {draftJobs.map((job) => (
                                <Chip
                                    key={String(job.jobId)}
                                    size="small"
                                    label={getJobLabel(job, language)}
                                    onDelete={() => onToggleJob(job)}
                                />
                            ))}
                        </Box>
                    )}
                    <List className={classes.jobs}>
                        {jobResults.map((job) => {
                            const jobId = String(job.jobId || "");
                            const checked = draftJobs.some(
                                (item) => String(item.jobId) === jobId
                            );
                            const id = optionId("job", jobId);

                            return (
                                <ListItem
                                    key={jobId}
                                    button
                                    disabled={jobFiltersDisabled}
                                    selected={checked}
                                    onClick={() => onToggleJob(job)}
                                >
                                    <Checkbox
                                        edge="start"
                                        checked={checked}
                                        disabled={jobFiltersDisabled}
                                        tabIndex={-1}
                                        disableRipple
                                        inputProps={{ "aria-labelledby": id }}
                                    />
                                    <ListItemText
                                        id={id}
                                        primary={
                                            getJobTitle(job, language) || "-"
                                        }
                                        secondary={getDisplayText(
                                            job.jobId,
                                            language
                                        )}
                                    />
                                </ListItem>
                            );
                        })}
                        {jobLoading && jobResults.length === 0 && (
                            <ListItem>
                                <Typography className={classes.muted}>
                                    {t("searchingJobs")}
                                </Typography>
                            </ListItem>
                        )}
                        {!jobLoading &&
                            jobSearchExecuted &&
                            jobResults.length === 0 && (
                                <ListItem>
                                    <Typography className={classes.muted}>
                                        {t("noJobFound")}
                                    </Typography>
                                </ListItem>
                            )}
                        {!jobSearchExecuted && jobSearch.trim().length < 4 && (
                            <ListItem>
                                <Typography className={classes.muted}>
                                    {t("typeAtLeastFourChars")}
                                </Typography>
                            </ListItem>
                        )}
                    </List>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClear}>{t("clear")}</Button>
                <Button onClick={onClose}>{t("cancel")}</Button>
                <Button color="primary" variant="contained" onClick={onApply}>
                    {t("filter")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WhatsappInboxFilterDialog;
